import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Container,
} from "@mui/material";
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Science as ScienceIcon,
} from "@mui/icons-material";
import axios from "axios";
import FileUploadComponent from "../../../components/FileUploadComponent";
import { decryptText, decryptAppId } from "../../../utils/encryption";
import s3Utils from "../../../utils/s3Utils";
import PageTitle from '../../../components/PageTitle';
import useAssessmentTracking from '../../../hooks/useAssessmentTracking';
import { LocationWarningDialog, HourlyAttendanceDialog } from '../../../components/AssessmentTrackingDialogs';

const TestWitness = () => {
  const location = useLocation();

  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get("orgId");
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        console.log("📍 Using decrypted orgId from URL query:", decryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        return encryptedOrgId;
      }
    }
    return null;
  };

  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        return encryptedAppId;
      }
    }
    return null;
  };

  // Function to get assessmentId from URL query parameters with decryption
  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get("assessmentId");
    if (encryptedAssessmentId) {
      try {
        const decryptedAssessmentId = decryptText(encryptedAssessmentId);
        console.log(
          "📍 Using decrypted assessmentId from URL query:",
          decryptedAssessmentId
        );
        return decryptedAssessmentId;
      } catch (error) {
        return encryptedAssessmentId;
      }
    }
    return null;
  };

  // Function to get assessorId from URL query parameters with decryption
  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        const decryptedAssessorId = decryptText(encryptedAssessorId);
        console.log(
          "📍 Using decrypted assessorId from URL query:",
          decryptedAssessorId
        );
        return decryptedAssessorId;
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return null;
  };

  // Function to get assessorId from URL query parameters with decryption
  const getLabTypeFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedLabType = searchParams.get("labType");
    if (encryptedLabType) {
      try {
        const decryptedLabType = decryptText(encryptedLabType);
        console.log(
          "📍 Using decrypted labType from URL query:",
          decryptedLabType
        );
        return decryptedLabType;
      } catch (error) {
        return encryptedLabType;
      }
    }
    return null;
  };

  const getOrgId = () => {
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      return queryOrgId;
    }
    return null;
  };

  const getAppId = () => {
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      return queryAppId;
    }
    return null;
  };

  const getAssessmentId = () => {
    const queryAssessmentId = getAssessmentIdFromQuery();
    if (queryAssessmentId) {
      return queryAssessmentId;
    }
    return null;
  };

  const getAssessorId = () => {
    const queryAssessorId = getAssessorIdFromQuery();
    if (queryAssessorId) {
      return queryAssessorId;
    }
    return null;
  };

  const getLabType = () => {
    const queryLabType = getLabTypeFromQuery();
    if (queryLabType) {
      return queryLabType;
    }
    return null;
  };

  const [orgId, setOrgId] = useState(getOrgId());
  const [appId, setAppId] = useState(getAppId());
  const [assessmentId, setAssessmentId] = useState(getAssessmentId());
  const [assessorId, setAssessorId] = useState(getAssessorId());
  const [labType, setLabType] = useState(getLabType());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);

  // Assessment tracking hook for location and hourly attendance
  const {
    locationWarningOpen,
    distanceMessage,
    attendanceCheckDialogOpen,
    attendanceCheckMessage,
    setLocationWarningOpen,
    setAttendanceCheckDialogOpen,
    checkHourlyAttendance,
  } = useAssessmentTracking({
    assessorId,
    appId,
    orgId,
  });

  // Test Witness State
  const [testWitnesses, setTestWitnesses] = useState([]);
  const [testWitnessForm, setTestWitnessForm] = useState({
    title: "",
    remarks: "",
    document: null,
  });
  const [editingTestWitness, setEditingTestWitness] = useState(null);
  const [fileUploadKey, setFileUploadKey] = useState(0); // Key to force FileUploadComponent reset

  // Get URL parameters
  useEffect(() => {
    loadTestWitnesses(appId, assessmentId, assessorId);
  }, [appId, orgId, assessmentId, assessorId, labType]);

  // Load Test Witnesses from API
  const loadTestWitnesses = async (appId, assessmentId, assessorId) => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetTestwitness?appid=${appId}&assessmentid=${assessmentId}&assessorid=${assessorId}`;
      const resp = await axios.get(url);

      // Handle .NET API response format
      const witnessData = resp.data.isstatus
        ? resp.data.witness
        : resp.data.success
        ? resp.data.data
        : [];

      if (witnessData && Array.isArray(witnessData)) {
        const formattedData = await Promise.all(
          witnessData.map(async (data) => {
            let document = data.file;

            if (s3Utils.isJustFilename(data.file)) {
              document = await s3Utils.generateSignedUrlForFilename(data.file);
            }

            return {
              id: data.id,
              title: data.tittle,
              remarks: data.remarks,
              document: document,
            };
          })
        );

        setTestWitnesses(formattedData);
      }
    } catch (error) {
      console.error("❌ Error loading test witnesses:", error);
      showSnackbar("Error loading test witnesses", "error");
    } finally {
      setLoading(false);
    }
  };

  // Show Snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Handle form input change
  const handleInputChange = (field) => (event) => {
    setTestWitnessForm({
      ...testWitnessForm,
      [field]: event.target.value,
    });
  };

  // Handle Add/Update Test Witness
  const handleSubmitTestWitness = async (event) => {
    event.preventDefault();

    if (!testWitnessForm.title) {
      showSnackbar("Title is required", "error");
      return;
    }

    if (!appId || !orgId || !assessmentId || !assessorId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        appid: String(appId),
        assessmentid: String(assessmentId),
        assessorid: String(assessorId),
        orgid: String(orgId),
        tittle: testWitnessForm.title, // API expects 'tittle' (with double t)
        remarks: testWitnessForm.remarks || "",
        file: testWitnessForm.document || "",
        labtypeid: labType, // Default lab type ID
      };

      console.log("📤 Saving test witness with payload:", payload);

      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Testwitness`;
      const response = await axios.post(url, payload);

      // Handle .NET API response format
      if (response.data.success || response.data.isstatus) {
        showSnackbar(
          editingTestWitness
            ? "Test witness updated successfully"
            : "Test witness added successfully",
          "success"
        );

        // Reset form
        setTestWitnessForm({
          title: "",
          remarks: "",
          document: null,
        });
        setEditingTestWitness(null);
        setFileUploadKey(prev => prev + 1); // Force FileUploadComponent to reset

        // Reload test witnesses
        loadTestWitnesses(appId, assessmentId, assessorId);
      } else {
        throw new Error(response.data.message || "Failed to save test witness");
      }
    } catch (error) {
      console.error("❌ Error saving test witness:", error);
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          "Error saving test witness",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle Edit Test Witness
  const handleEditTestWitness = (id) => {
    const witness = testWitnesses.find((w) => w.id === id);
    if (witness) {
      setTestWitnessForm({
        title: witness.title,
        remarks: witness.remarks,
        document: witness.document,
      });
      setEditingTestWitness(id);
    }
  };

  // Handle Delete Test Witness
  const handleDeleteTestWitness = async (id) => {
    if (!window.confirm("Are you sure you want to delete this test witness?")) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: String(id),
        appid: String(appId),
      };

      console.log("🗑️ Deleting test witness:", payload);

      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/DeleteTestwitness`;
      const response = await axios.delete(url, { data: payload });

      // Handle .NET API response format
      if (response.data.success || response.data.isstatus) {
        showSnackbar("Test witness deleted successfully", "success");
        loadTestWitnesses(appId, assessmentId, assessorId);
      } else {
        throw new Error(
          response.data.message || "Failed to delete test witness"
        );
      }
    } catch (error) {
      console.error("❌ Error deleting test witness:", error);
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          "Error deleting test witness",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <PageTitle title="NABL | Test Witness" />
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ScienceIcon sx={{ fontSize: 40 }} />
          Test Witness
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Add and manage test witness details for assessment
        </Typography>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Add/Edit Test Witness Form */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        <CardContent>
          <Typography
            variant="h6"
            sx={{ mb: 3, color: "primary.main", fontWeight: 600 }}
          >
            {editingTestWitness ? "Edit Test Witness" : "Add Test Witness"}
          </Typography>

          <form onSubmit={handleSubmitTestWitness}>
            <Grid container spacing={3}>
              {/* Title */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Title"
                  value={testWitnessForm.title}
                  onChange={handleInputChange("title")}
                  variant="outlined"
                  placeholder="Enter test witness title"
                />
              </Grid>

              {/* Remarks */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Remarks"
                  value={testWitnessForm.remarks}
                  onChange={handleInputChange("remarks")}
                  variant="outlined"
                  placeholder="Enter remarks"
                  multiline
                  rows={1}
                />
              </Grid>

              {/* Document Upload */}
              <Grid item xs={12}>
                <FileUploadComponent
                  key={fileUploadKey}
                  fieldName="document"
                  label="Upload Document"
                  orgId={assessorId}
                  section="test-witness-documents"
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSize={5}
                  multiple={false}
                  onChange={(fieldName, fileData) => {
                    setTestWitnessForm((prev) => ({
                      ...prev,
                      document: fileData.fileName,
                    }));
                  }}
                  initialFile={testWitnessForm.document}
                />
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={editingTestWitness ? <SaveIcon /> : <AddIcon />}
                  >
                    {editingTestWitness
                      ? "Update Test Witness"
                      : "Add Test Witness"}
                  </Button>
                  {editingTestWitness && (
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => {
                        setTestWitnessForm({
                          title: "",
                          remarks: "",
                          document: null,
                        });
                        setEditingTestWitness(null);
                        setFileUploadKey(prev => prev + 1); // Force FileUploadComponent to reset
                      }}
                    >
                      Cancel Edit
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Test Witnesses Table */}
      <Box>
        <Typography variant="h6" sx={{ mb: 3, color: "text.primary" }}>
          Test Witnesses ({testWitnesses.length})
        </Typography>

        {testWitnesses.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "primary.main" }}>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    #
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Title
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Remarks
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Document
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {testWitnesses.map((witness, index) => (
                  <TableRow key={witness.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                        {witness.title}
                      </Typography>
                    </TableCell>
                    <TableCell>{witness.remarks || "-"}</TableCell>
                    <TableCell>
                      {witness.document ? (
                        <Tooltip title={witness.document}>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => {
                              if (witness.document) {
                                window.open(witness.document, "_blank");
                              }
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {/* <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            onClick={() => handleEditTestWitness(witness.id)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip> */}
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteTestWitness(witness.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Card variant="outlined" sx={{ textAlign: "center", py: 4 }}>
            <ScienceIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary">
              No Test Witnesses Added
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Add your first test witness using the form above.
            </Typography>
          </Card>
        )}
      </Box>

      {/* Location Warning Dialog */}
      <LocationWarningDialog
        open={locationWarningOpen}
        onClose={() => setLocationWarningOpen(false)}
        distanceMessage={distanceMessage}
      />

      {/* Hourly Attendance Dialog */}
      <HourlyAttendanceDialog
        open={attendanceCheckDialogOpen}
        onClose={() => setAttendanceCheckDialogOpen(false)}
        message={attendanceCheckMessage}
        onRecheck={checkHourlyAttendance}
      />
    </Container>
    </>
  );
};

export default TestWitness;
